import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { businessInfo } from '../mockData';
import { siteConfig } from '../content/siteContent';
import { Button } from './ui/button';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { path: '/emergency-locksmith', label: 'Emergency' },
    { path: '/residential', label: 'Residential' },
    { path: '/commercial', label: 'Commercial' },
    { path: '/pricing', label: 'Pricing' },
    { path: '/partners', label: 'Trade Partners' },
    { path: '/projects', label: 'Projects' },
    { path: '/about', label: 'About' },
    { path: '/contact', label: 'Contact' },
  ];

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(`${path}/`);

  return (
    <>
      <nav className={`fixed w-full top-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
      }`}>
        {/* Top bar - hidden on mobile */}
        <div className="hidden lg:block bg-blue-900 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-2 text-sm">
              <span className="text-blue-200">{siteConfig.hoursLine} · Mobile — we come to you</span>
              <div className="flex items-center">
                <a href={`tel:${businessInfo.phoneTel}`} className="flex items-center gap-2 hover:text-blue-200 transition-colors mr-6 font-semibold">
                  <Phone size={16} />
                  {businessInfo.phone}
                </a>
                <a href={`mailto:${businessInfo.email}`} className="flex items-center gap-2 hover:text-blue-200 transition-colors">
                  <Mail size={16} />
                  {businessInfo.email}
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Main navbar */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20 sm:h-24 lg:h-28">
            {/* Brand: existing logo + interim "LOCKSMITHS" wordmark extension (plan D6/D10 —
                replace with the updated logo lockup when artwork arrives) */}
            <Link
              to="/"
              className="flex items-center gap-2 flex-shrink-0 transition-transform duration-300 ease-out hover:scale-105"
              data-testid="brand-logo-link"
            >
              <img
                src="/images/logo-header.png"
                alt="Shore Doors Locksmiths - mobile locksmiths and commercial door hardware, Auckland"
                className="w-auto object-contain"
                style={{ height: 'clamp(52px, 8vw, 88px)', width: 'auto' }}
                data-testid="header-logo-img"
              />
              <span className="text-blue-900 font-bold tracking-[0.18em] text-xs sm:text-sm uppercase border-l border-gray-300 pl-2 sm:pl-3">
                Locksmiths
              </span>
            </Link>

            {/* Desktop navigation */}
            <div className="hidden lg:flex items-center gap-1 xl:gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`px-3 py-2 rounded-md text-[15px] leading-relaxed transition-all duration-200 relative ${
                    isActive(link.path)
                      ? 'text-blue-600 font-semibold bg-blue-50'
                      : 'text-gray-600 font-medium hover:text-blue-600 hover:bg-gray-50'
                  }`}
                >
                  {link.label}
                  {isActive(link.path) && (
                    <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-blue-600 rounded-full"></span>
                  )}
                </Link>
              ))}
              <a href={`tel:${businessInfo.phoneTel}`} data-testid="nav-cta-call" className="ml-3">
                <Button className="bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 text-sm font-bold shadow-sm hover:shadow-md transition-all">
                  <Phone className="mr-2" size={16} />
                  {businessInfo.phone}
                </Button>
              </a>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2.5 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg max-h-[calc(100vh-5rem)] overflow-y-auto">
            <div className="px-4 py-5 space-y-2">
              <Link
                to="/"
                className={`block px-4 py-3 rounded-lg text-base transition-all ${
                  location.pathname === '/'
                    ? 'bg-blue-50 text-blue-600 font-semibold'
                    : 'text-gray-700 font-medium hover:bg-gray-50 hover:text-blue-600'
                }`}
              >
                Home
              </Link>
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block px-4 py-3 rounded-lg text-base transition-all ${
                    isActive(link.path)
                      ? 'bg-blue-50 text-blue-600 font-semibold'
                      : 'text-gray-700 font-medium hover:bg-gray-50 hover:text-blue-600'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-4 mt-2 border-t border-gray-100 space-y-3">
                <a href={`tel:${businessInfo.phoneTel}`} className="block">
                  <Button className="w-full bg-red-600 hover:bg-red-700 text-white py-3 font-bold">
                    <Phone className="mr-2" size={18} />
                    Call {businessInfo.phone}
                  </Button>
                </a>
                <a href={`mailto:${businessInfo.email}`} className="flex items-center gap-3 px-4 py-2 text-gray-600 hover:text-blue-600 transition-colors">
                  <Mail size={18} />
                  <span className="font-medium">{businessInfo.email}</span>
                </a>
              </div>
            </div>
          </div>
        )}
      </nav>
      {/* Spacer to prevent content from hiding under fixed navbar */}
      <div className="h-20 sm:h-24 lg:h-[148px]"></div>
    </>
  );
};

export default Navbar;
